import React from 'react';
import { useDispatch } from 'react-redux';

import { useTranslation } from '~/app';
import { Button } from '~/components/Form';
import { resetConnectionTimeout } from '~/game';

interface ConnectionTimeoutDialogProps {
  open: boolean;
  onClose: () => void;
  onHidePermanently: () => void;
  onNotNow: () => void;
}

const ConnectionTimeoutDialog = ({
  open,
  onClose,
  onHidePermanently,
  onNotNow,
}: ConnectionTimeoutDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleReload = () => {
    window.location.reload();
    onClose();
  };

  const handleHidePermanently = () => {
    onHidePermanently();
    onClose();
  };

  const handleNotNow = () => {
    dispatch(resetConnectionTimeout());
    onNotNow();
    onClose();
  };

  if (!open) return <></>;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-6 py-5 shadow-xl">
      <div className="mb-4 max-w-sm text-sm text-gray-700 md:max-w-lg">
        {t('game_connectionTimeoutText')}
      </div>

      {/* Responsive button layout: horizontal on larger screens, vertical on small screens */}
      <div className="flex flex-col justify-end gap-2 sm:flex-row sm:gap-3">
        <Button
          variant="primary"
          onClick={handleReload}
          className="flex items-center justify-center space-x-2 bg-primary px-3 py-2 whitespace-nowrap text-white"
          size="sm"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="text-sm">{t('game_connectionTimeoutReload')}</span>
        </Button>

        <Button
          variant="secondary"
          onClick={handleNotNow}
          className="flex items-center justify-center space-x-2 border border-gray-300 bg-white px-3 py-2 whitespace-nowrap text-gray-700"
          size="sm"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <span className="text-sm">{t('game_connectionTimeoutNotNow')}</span>
        </Button>

        <Button
          variant="secondary"
          onClick={handleHidePermanently}
          className="flex items-center justify-center space-x-2 border border-gray-300 bg-white px-3 py-2 whitespace-nowrap text-gray-700"
          size="sm"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
            />
          </svg>
          <span className="text-sm">{t('game_connectionTimeoutHide')}</span>
        </Button>
      </div>
    </div>
  );
};

export default ConnectionTimeoutDialog;
